import { formatTitle, preloadLogo, drawWrappedText, loadAndDrawLogo } from './render-utils';
import { drawRoundedRect } from './canvas-utils';
import { toast } from '@/hooks/use-toast';

interface TemplateOptions {
  title: string;
  drawingImage: string;
  analysisText?: string | null;
  date?: Date;
  watermarkText?: string;
  bgColor?: string;
  textColor?: string;
  accentColor?: string;
}

// Higher resolution dimensions for portrait format
const EXPORT_WIDTH = 2880;  // Doubled from 1440 for higher resolution
const EXPORT_HEIGHT = 3840; // Doubled from 1920 for higher resolution
const PADDING = 80;         // Scaled up padding
const HEADER_HEIGHT = 240;  // Scaled up header
const FOOTER_HEIGHT = 160;  // Scaled up footer

/**
 * Renders a drawing with a professional, content-focused design
 * with enhanced resolution for maximum quality
 */
export const renderJpegTemplate = async (options: TemplateOptions): Promise<string> => {
  try {
    const {
      title,
      drawingImage,
      analysisText,
      date = new Date(),
      watermarkText = "Created with Surgical Drawing Studio",
      bgColor = "#ffffff",
      textColor = "#1A1F2C",
      accentColor = "#8B5CF6"
    } = options;

    // Create high-resolution canvas with portrait dimensions
    const canvas = document.createElement('canvas');
    canvas.width = EXPORT_WIDTH;
    canvas.height = EXPORT_HEIGHT;
    
    const ctx = canvas.getContext('2d', {
      alpha: true,
      desynchronized: false, // Better image quality over performance
      willReadFrequently: false
    });
    
    if (!ctx) {
      throw new Error('Could not get canvas context');
    }
    
    // Enhanced device pixel ratio handling
    const dpr = window.devicePixelRatio || 2; // Use minimum 2x for high resolution
    if (dpr > 1) {
      const logicalWidth = canvas.width;
      const logicalHeight = canvas.height;
      
      // Set actual dimensions even higher for better quality
      canvas.width = logicalWidth * dpr;
      canvas.height = logicalHeight * dpr;
      canvas.style.width = `${logicalWidth}px`;
      canvas.style.height = `${logicalHeight}px`;
      
      // Scale everything by DPR
      ctx.scale(dpr, dpr);
    }
    
    // Maximize image quality settings
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    
    // Improved anti-aliasing for text and shapes
    ctx.shadowBlur = 0; // Remove default shadow which can cause blur
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.miterLimit = 2;
    
    // Draw subtle gradient background
    const gradient = ctx.createLinearGradient(0, 0, 0, EXPORT_HEIGHT);
    gradient.addColorStop(0, '#ffffff');
    gradient.addColorStop(1, '#f9f7ff');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, EXPORT_WIDTH, EXPORT_HEIGHT);
    
    // Add subtle pattern overlay for texture (with higher resolution pattern)
    ctx.globalAlpha = 0.02;
    ctx.fillStyle = '#6366f1';
    for (let x = 0; x < EXPORT_WIDTH; x += 40) { // Increased pattern density
      for (let y = 0; y < EXPORT_HEIGHT; y += 40) {
        ctx.fillRect(x, y, 2, 2); // Larger dots for higher resolution
      }
    }
    ctx.globalAlpha = 1;
    
    // Load logo for branding
    const logoDataUrl = await preloadLogo();
    
    // Draw elegant header with title and logo
    await drawEnhancedHeader(ctx, {
      title: formatTitle(title),
      logoUrl: logoDataUrl,
      width: EXPORT_WIDTH,
      height: HEADER_HEIGHT,
      padding: PADDING,
      textColor,
      accentColor
    });
    
    // Extract and draw the main drawing content with higher resolution
    const drawingY = HEADER_HEIGHT + PADDING;
    const availableHeight = EXPORT_HEIGHT - drawingY - FOOTER_HEIGHT - (analysisText ? 400 : 0) - PADDING * 2;
    const availableWidth = EXPORT_WIDTH - PADDING * 2;
    
    // Draw content with enhanced resolution processing
    const contentHeight = await drawHighResolutionContent(
      ctx, 
      drawingImage, 
      PADDING, 
      drawingY, 
      availableWidth, 
      availableHeight
    );
    
    let currentY = drawingY + contentHeight + PADDING;
    
    // Draw analysis section if provided with enhanced visual design
    if (analysisText) {
      currentY = await drawEnhancedAnalysisSection(
        ctx, 
        analysisText, 
        PADDING, 
        currentY,
        EXPORT_WIDTH - (PADDING * 2),
        textColor,
        accentColor
      );
    }
    
    // Draw professional footer
    drawEnhancedFooter(ctx, {
      watermarkText,
      date,
      width: EXPORT_WIDTH,
      height: FOOTER_HEIGHT,
      y: EXPORT_HEIGHT - FOOTER_HEIGHT,
      padding: PADDING,
      textColor,
      accentColor
    });
    
    // Export at maximum quality JPEG
    return canvas.toDataURL('image/jpeg', 1.0);
  } catch (error) {
    console.error('Error rendering high-resolution JPEG template:', error);
    toast({
      title: "Export Error",
      description: "There was a problem creating your image export",
      variant: "destructive"
    });
    throw error;
  }
};

// Draw elegant header with modern styling
const drawEnhancedHeader = async (
  ctx: CanvasRenderingContext2D,
  options: {
    title: string;
    logoUrl: string | null;
    width: number;
    height: number;
    padding: number;
    textColor: string;
    accentColor: string;
  }
): Promise<void> => {
  const { title, logoUrl, width, height, padding, textColor, accentColor } = options;
  
  // Add subtle shadow to header
  ctx.save();
  ctx.shadowColor = 'rgba(0, 0, 0, 0.05)';
  ctx.shadowBlur = 20; // Increased blur for better resolution
  ctx.shadowOffsetY = 4;
  
  // Draw header background with subtle gradient
  const headerGradient = ctx.createLinearGradient(0, 0, width, 0);
  headerGradient.addColorStop(0, '#ffffff');
  headerGradient.addColorStop(1, '#fcfaff');
  ctx.fillStyle = headerGradient;
  drawRoundedRect(ctx, padding/2, padding/2, width - padding, height - padding, 20, true, false);
  ctx.restore();
  
  // Draw accent line with higher precision
  const accentGradient = ctx.createLinearGradient(padding, 0, padding + 240, 0);
  accentGradient.addColorStop(0, accentColor);
  accentGradient.addColorStop(1, '#7C65DE');
  ctx.fillStyle = accentGradient;
  drawRoundedRect(ctx, padding, padding * 1.5, 12, height - padding * 3, 6, true, false);
  
  // Draw title text with professional typography and proper scaling
  ctx.font = 'bold 76px sans-serif'; // Doubled font size for higher resolution
  ctx.fillStyle = textColor;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(title, padding + 40, height / 2);
  
  // Draw logo if available (scaled up for high resolution)
  if (logoUrl) {
    await loadAndDrawLogo(ctx, logoUrl, width, height, 140); // Larger logo
  }
  
  // Add subtle separator line
  ctx.strokeStyle = '#f0f0f0';
  ctx.lineWidth = 2; // Thicker line for higher resolution
  ctx.beginPath();
  ctx.moveTo(padding, height - 2);
  ctx.lineTo(width - padding, height - 2);
  ctx.stroke();
};

// Optimized content drawing with enhanced resolution processing
const drawHighResolutionContent = (
  ctx: CanvasRenderingContext2D,
  imageUrl: string,
  x: number,
  y: number,
  maxWidth: number,
  maxHeight: number,
): Promise<number> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        // Create a temporary canvas to process the image at original resolution
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = img.width;
        tempCanvas.height = img.height;
        const tempCtx = tempCanvas.getContext('2d', {
          alpha: true,
          desynchronized: false,
          willReadFrequently: true
        });
        
        if (!tempCtx) {
          reject(new Error('Could not get temporary canvas context'));
          return;
        }
        
        // Apply high-quality settings to temp canvas
        tempCtx.imageSmoothingEnabled = true;
        tempCtx.imageSmoothingQuality = 'high';
        
        // Draw the image to the temp canvas at 1:1 resolution
        tempCtx.drawImage(img, 0, 0);
        
        // Find the actual bounds of the drawing content (non-white pixels)
        // This algorithm is more precise to detect drawing content
        const imageData = tempCtx.getImageData(0, 0, img.width, img.height);
        const bounds = findContentBoundsEnhanced(imageData);
        
        // If no content found, draw the entire image centered with high quality
        if (!bounds.found) {
          console.log('No clear content bounds found, drawing full image in high resolution');
          const aspectRatio = img.width / img.height;
          let width, height;
          
          if (aspectRatio > maxWidth / maxHeight) {
            // Image is wider than available space
            width = maxWidth;
            height = width / aspectRatio;
          } else {
            // Image is taller than available space
            height = maxHeight;
            width = height * aspectRatio;
          }
          
          // Center the image
          const xOffset = x + (maxWidth - width) / 2;
          const yOffset = y + (maxHeight - height) / 2;
          
          // Add shadow for depth
          ctx.save();
          ctx.shadowColor = 'rgba(0, 0, 0, 0.15)';
          ctx.shadowBlur = 40; // Enhanced shadow for high resolution
          ctx.shadowOffsetY = 20;
          
          // Draw background for the image
          ctx.fillStyle = '#ffffff';
          drawRoundedRect(ctx, xOffset - 20, yOffset - 20, width + 40, height + 40, 16, true, false);
          ctx.restore();
          
          // Draw the image with enhanced quality settings
          ctx.drawImage(img, xOffset, yOffset, width, height);
          
          resolve(height);
          return;
        }
        
        // Extract just the content area with appropriate padding
        const padding = Math.min(bounds.width, bounds.height) * 0.15; // 15% padding
        const extractedWidth = bounds.width + padding * 2;
        const extractedHeight = bounds.height + padding * 2;
        
        // Draw extracted content to a new high-resolution canvas to maintain quality
        const contentCanvas = document.createElement('canvas');
        contentCanvas.width = extractedWidth * 2; // 2x size for higher resolution
        contentCanvas.height = extractedHeight * 2;
        
        const contentCtx = contentCanvas.getContext('2d', {
          alpha: true,
          desynchronized: false
        });
        
        if (!contentCtx) {
          reject(new Error('Could not get content canvas context'));
          return;
        }
        
        // Apply high-quality settings
        contentCtx.imageSmoothingEnabled = true;
        contentCtx.imageSmoothingQuality = 'high';
        contentCtx.scale(2, 2); // Scale up for high resolution
        
        // Extract and draw content at higher resolution
        contentCtx.drawImage(
          img, 
          bounds.left - padding, 
          bounds.top - padding, 
          extractedWidth, 
          extractedHeight,
          0, 
          0, 
          extractedWidth, 
          extractedHeight
        );
        
        // Calculate dimensions to optimize the extracted content display
        const contentAspectRatio = extractedWidth / extractedHeight;
        let displayWidth, displayHeight;
        
        if (contentAspectRatio > maxWidth / maxHeight) {
          // Content is wider than available space
          displayWidth = maxWidth;
          displayHeight = displayWidth / contentAspectRatio;
        } else {
          // Content is taller than available space
          displayHeight = maxHeight;
          displayWidth = displayHeight * contentAspectRatio;
        }
        
        // Center the content
        const xOffset = x + (maxWidth - displayWidth) / 2;
        const yOffset = y + (maxHeight - displayHeight) / 2;
        
        // Add shadow for depth
        ctx.save();
        ctx.shadowColor = 'rgba(0, 0, 0, 0.15)';
        ctx.shadowBlur = 40; // Enhanced shadow blur
        ctx.shadowOffsetY = 20;
        
        // Draw background for the content
        ctx.fillStyle = '#ffffff';
        drawRoundedRect(ctx, xOffset - 20, yOffset - 20, displayWidth + 40, displayHeight + 40, 16, true, false);
        ctx.restore();
        
        // Draw just the content area from the high-resolution content canvas
        ctx.drawImage(
          contentCanvas,
          0,
          0,
          contentCanvas.width,
          contentCanvas.height,
          xOffset, 
          yOffset, 
          displayWidth, 
          displayHeight
        );
        
        resolve(displayHeight + 40); // Add extra space
      } catch (error) {
        console.error('Error drawing high-resolution content:', error);
        reject(error);
      }
    };
    
    img.onerror = (error) => {
      console.error('Error loading image:', error);
      reject(error);
    };
    
    img.src = imageUrl;
  });
};

// Enhanced content bounds detection algorithm
const findContentBoundsEnhanced = (imageData: ImageData) => {
  const { data, width, height } = imageData;
  let minX = width;
  let minY = height;
  let maxX = 0;
  let maxY = 0;
  let found = false;
  
  // Sensitivity parameters for content detection
  const threshold = 245; // Slightly higher threshold (closer to white)
  const alphaThreshold = 20;  // Minimum alpha to consider pixel non-transparent
  const scanStep = 1;   // Check every pixel for maximum precision
  
  for (let y = 0; y < height; y += scanStep) {
    for (let x = 0; x < width; x += scanStep) {
      const idx = (y * width + x) * 4;
      
      // Check if pixel is not white/transparent using improved algorithm
      const r = data[idx];
      const g = data[idx + 1];
      const b = data[idx + 2];
      const a = data[idx + 3];
      
      // Average color luminance calculation
      const luminance = (r * 0.299 + g * 0.587 + b * 0.114);
      
      // Detect any non-white, visible pixel
      if (luminance < threshold && a > alphaThreshold) {
        minX = Math.min(x, minX);
        minY = Math.min(y, minY);
        maxX = Math.max(x, maxX);
        maxY = Math.max(y, maxY);
        found = true;
      }
    }
  }
  
  return {
    left: minX,
    top: minY,
    right: maxX,
    bottom: maxY,
    width: maxX - minX,
    height: maxY - minY,
    found
  };
};

// Completely redesigned analysis section with modern UI design
const drawEnhancedAnalysisSection = async (
  ctx: CanvasRenderingContext2D,
  analysisText: string,
  x: number,
  y: number,
  width: number,
  textColor: string,
  accentColor: string
): Promise<number> => {
  const padding = 80; // Increased padding for higher resolution
  
  // Add shadow for depth with enhanced visual appeal
  ctx.save();
  ctx.shadowColor = 'rgba(0, 0, 0, 0.12)';
  ctx.shadowBlur = 40; // Enhanced shadow blur
  ctx.shadowOffsetY = 15;
  
  // Draw analysis card background with premium gradient and rounded corners
  const cardHeight = 380; // Increased minimum height for higher resolution
  
  // Create premium gradient background for the analysis section
  const gradient = ctx.createLinearGradient(x, y, x + width, y + cardHeight);
  gradient.addColorStop(0, '#ffffff');
  gradient.addColorStop(1, '#f9f8ff');
  ctx.fillStyle = gradient;
  
  // Draw a more elegant card with larger corner radius
  drawRoundedRect(ctx, x, y, width, cardHeight, 30, true, false);
  ctx.restore();
  
  // Add subtle decorative pattern to card
  ctx.globalAlpha = 0.03;
  ctx.fillStyle = accentColor;
  
  // Create a more organized, grid-like pattern
  for (let i = x; i < x + width; i += 70) {
    for (let j = y; j < y + cardHeight; j += 70) {
      ctx.beginPath();
      ctx.arc(i, j, 2, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.globalAlpha = 1;
  
  // Add AI icon or decorative accent
  ctx.save();
  const iconRadius = 50;
  const iconX = x + padding;
  const iconY = y + padding;
  
  // Draw glowing circle for AI icon
  const iconGradient = ctx.createRadialGradient(
    iconX, iconY, 0,
    iconX, iconY, iconRadius
  );
  
  iconGradient.addColorStop(0, 'rgba(139, 92, 246, 0.15)');
  iconGradient.addColorStop(0.5, 'rgba(139, 92, 246, 0.08)');
  iconGradient.addColorStop(1, 'rgba(139, 92, 246, 0)');
  
  ctx.fillStyle = iconGradient;
  ctx.beginPath();
  ctx.arc(iconX, iconY, iconRadius, 0, Math.PI * 2);
  ctx.fill();
  
  // Draw "AI" text inside the glowing circle
  ctx.font = 'bold 40px sans-serif';
  const textGradient = ctx.createLinearGradient(iconX - 20, iconY, iconX + 20, iconY);
  textGradient.addColorStop(0, '#8B5CF6');
  textGradient.addColorStop(1, '#7C65DE');
  
  ctx.fillStyle = textGradient;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('AI', iconX, iconY);
  ctx.restore();
  
  // Draw heading with premium typography and accent
  const headingGradient = ctx.createLinearGradient(x + iconRadius * 2 + padding, y + padding - 20, x + iconRadius * 2 + padding + 300, y + padding - 20);
  headingGradient.addColorStop(0, '#8B5CF6');
  headingGradient.addColorStop(1, '#7C65DE');
  
  ctx.font = 'bold 60px sans-serif'; // Larger font size for better visibility
  ctx.fillStyle = headingGradient;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText("Analysis", x + iconRadius * 2 + padding, y + padding);
  
  // Draw decorative accent line under heading
  ctx.beginPath();
  ctx.strokeStyle = headingGradient;
  ctx.lineWidth = 5; // Thicker line
  ctx.lineCap = 'round';
  ctx.moveTo(x + iconRadius * 2 + padding, y + padding + 30);
  ctx.lineTo(x + iconRadius * 2 + padding + 200, y + padding + 30);
  ctx.stroke();
  
  // Add subtle top border accent
  const borderGradient = ctx.createLinearGradient(x, y, x + width, y);
  borderGradient.addColorStop(0, '#8B5CF6');
  borderGradient.addColorStop(0.5, '#D6BCFA');
  borderGradient.addColorStop(1, '#8B5CF6');
  
  ctx.beginPath();
  ctx.lineWidth = 5;
  ctx.strokeStyle = borderGradient;
  ctx.moveTo(x + 40, y);
  ctx.lineTo(x + width - 40, y);
  ctx.stroke();
  
  // Draw analysis content with improved typography and bullet points
  ctx.font = '36px sans-serif'; // Larger font size for better readability
  ctx.fillStyle = textColor;
  
  // Get key points from analysis text and format them nicely
  const points = analysisText
    .split('\n')
    .filter(p => p.trim().length > 0)
    .slice(0, 4); // Limit to 4 key points for better layout
  
  let currentY = y + padding + 90; // Start below the heading
  
  // Draw points with modern styled bullets
  for (const point of points) {
    // Draw custom bullet point with gradient
    const bulletGradient = ctx.createLinearGradient(
      x + padding, currentY,
      x + padding, currentY + 20
    );
    bulletGradient.addColorStop(0, '#8B5CF6');
    bulletGradient.addColorStop(1, '#7C65DE');
    
    ctx.fillStyle = bulletGradient;
    ctx.beginPath();
    ctx.arc(x + padding + 10, currentY + 17, 10, 0, Math.PI * 2);
    ctx.fill();
    
    // Add subtle shine effect to bullet
    ctx.beginPath();
    ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
    ctx.arc(x + padding + 7, currentY + 14, 3, 0, Math.PI * 2);
    ctx.fill();
    
    // Draw point text with improved wrapping and styling
    ctx.fillStyle = textColor;
    const wrappedY = drawWrappedText(
      ctx, 
      point.trim(), 
      width - (padding * 2) - 40, 
      x + padding + 40, // More indent from bullet
      currentY, 
      52 // Increased line height for better readability
    );
    
    currentY = wrappedY + 30; // More space between points
  }
  
  // Ensure card is tall enough for content
  const finalCardHeight = Math.max(cardHeight, currentY - y + padding);
  
  // If card height changed, redraw the background
  if (finalCardHeight > cardHeight) {
    ctx.save();
    ctx.globalCompositeOperation = 'destination-over';
    
    const newGradient = ctx.createLinearGradient(x, y, x + width, y + finalCardHeight);
    newGradient.addColorStop(0, '#ffffff');
    newGradient.addColorStop(1, '#f9f8ff');
    ctx.fillStyle = newGradient;
    
    drawRoundedRect(ctx, x, y, width, finalCardHeight, 30, true, false);
    ctx.restore();
  }
  
  // Add decorative corner accent element
  ctx.save();
  ctx.globalAlpha = 0.1;
  ctx.fillStyle = '#8B5CF6';
  
  // Draw decorative corner shape
  ctx.beginPath();
  ctx.arc(x + width - 70, y + 70, 100, 0, Math.PI * 2);
  ctx.fill();
  
  ctx.globalAlpha = 0.05;
  ctx.beginPath();
  ctx.arc(x + width - 40, y + 40, 60, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  
  return y + finalCardHeight + padding;
};

// Draw enhanced footer with professional branding
const drawEnhancedFooter = (
  ctx: CanvasRenderingContext2D,
  options: {
    watermarkText: string;
    date: Date;
    width: number;
    height: number;
    y: number;
    padding: number;
    textColor: string;
    accentColor: string;
  }
): void => {
  const { watermarkText, date, width, height, y, padding, textColor, accentColor } = options;
  
  // Draw subtle separator
  const separatorGradient = ctx.createLinearGradient(padding, y, width - padding, y);
  separatorGradient.addColorStop(0, '#f0f0f0');
  separatorGradient.addColorStop(0.5, '#e0e0e0');
  separatorGradient.addColorStop(1, '#f0f0f0');
  
  ctx.strokeStyle = separatorGradient;
  ctx.lineWidth = 2; // Thicker line
  ctx.beginPath();
  ctx.moveTo(padding, y);
  ctx.lineTo(width - padding, y);
  ctx.stroke();
  
  // Draw watermark text with better typography
  ctx.font = 'bold 32px sans-serif'; // Doubled font size
  ctx.fillStyle = textColor + 'AA';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(watermarkText, padding, y + (height/2));
  
  // Draw date with improved formatting
  const dateText = date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
  ctx.textAlign = 'right';
  ctx.font = '30px sans-serif'; // Doubled font size
  ctx.fillStyle = textColor + '99';
  ctx.fillText(dateText, width - padding, y + (height/2));
};
